/**
 * Shared vocabulary for Phase 0 primitives.
 *
 * These types encode the "mandatory state matrix" from
 * docs/COMPONENT_LIBRARY.md: every major operational primitive must be able
 * to represent each of these states, because in real service conditions a
 * card is far more often loading/stale/disconnected than happily populated.
 */

/**
 * Generic data/interaction state a primitive can be in. RTL and dark mode are
 * handled orthogonally (via `dir` and `data-theme`), not as a `state` value,
 * so they are not listed here — every state below must still render
 * correctly in both directions and both themes.
 */
export type PrimitiveState =
  "loading" | "empty" | "populated" | "permission-restricted" | "provider-disconnected" | "partial-stale" | "failed" | "retrying";

export const PRIMITIVE_STATES: PrimitiveState[] = [
  "loading",
  "empty",
  "populated",
  "permission-restricted",
  "provider-disconnected",
  "partial-stale",
  "failed",
  "retrying",
];

/**
 * AI Action State vocabulary (docs/COMPONENT_LIBRARY.md → "AI Action State").
 *
 * IMPORTANT: this enumerates presentational states only. Phase 0 ships no
 * real AI behaviour — the AI panel primitive is a demo/state-machine display,
 * never wired to a live model or a real send/tool-call action.
 */
export type AiActionState =
  "suggested" | "drafted" | "sent-by-ai" | "sent-by-human" | "tool-action-confirmed" | "failed" | "human-review-required";

export const AI_ACTION_STATES: AiActionState[] = [
  "suggested",
  "drafted",
  "sent-by-ai",
  "sent-by-human",
  "tool-action-confirmed",
  "failed",
  "human-review-required",
];

export type StatusTone = "success" | "warning" | "danger" | "info" | "neutral";

export type DensityMode = "operations" | "standard" | "immersive";

export const DENSITY_MODES: DensityMode[] = ["operations", "standard", "immersive"];

export type ThemeMode = "light" | "dark";

export type Direction = "ltr" | "rtl";
